// src/components/modal/AuthModal.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogContent,
  Box,
  TextField,
  Button,
  Divider,
  Alert,
  FormHelperText,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Tabs,
  Tab,
  GlobalStyles,
  InputAdornment,
  Paper,
  Typography,
  IconButton,
} from "@mui/material";
import { alpha, styled } from "@mui/material/styles";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import GoogleIcon from "@mui/icons-material/Google";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import { RootState } from "../../store/store";
import {
  findEmailRequest,
  requestPasswordResetRequest,
  clearRecoveryState,
} from "../../store/slice/authSlice";
import { LEGAL_VERSIONS } from "../../legal/legalVersions";
import LegalDocumentDialog from "../legal/LegalDocumentDialog";
import { LegalDocType } from "../../legal/legalDocuments";

const REQUIRE_PHONE_ON_SIGNUP = false;
const ADULT_ONLY_14_PLUS = true;

type ConsentVersions = {
  terms: string;
  privacy: string;
  disclaimer: string;
  ageOver14: string;
  marketing: string;
};

type RecoveryMode = null | "find-id" | "reset-pw";
type AuthModalMode = "auth" | "password-edit";
type AuthModalTab = "login" | "signup";

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  mode?: AuthModalMode;
  isEditMode?: boolean;
  userEmail?: string;
  error?: string | null;

  onPasswordUpdate?: (form: { oldPassword: string; newPassword: string }) => void;
  onLogin?: (form: { email: string; password: string }) => void;

  onSignup?: (form: {
    email: string;
    password: string;
    confirmPassword: string;
    phone?: string;
    marketingEmailAgree: boolean;
    marketingSmsAgree: boolean;
    termsAgree: boolean;
    privacyAgree: boolean;
    disclaimerAgree: boolean;
    age14PlusAgree: boolean;
    consentVersions: ConsentVersions;
    turnstileToken: string;
  }) => void;

  onGoogleLogin?: () => void;
  onKakaoLogin?: () => void;
  onNaverLogin?: () => void;
  loading?: boolean;
  contextTitle?: string;
  contextDescription?: string;
  onReady?: () => void;
  onDismiss?: (detail: { reason: string; tab: AuthModalTab }) => void;
  onTabChange?: (tab: AuthModalTab) => void;
}

const CARD_BG = "#0b1522";
const SURFACE = "#0e1a2b";
const BORDER = "rgba(120,140,180,.18)";
const SUB_BORDER = "rgba(120,140,180,.14)";
const TEXT_MAIN = "#eaf2ff";
const TEXT_SUB = "#9fb4d8";
const PRIMARY_GRAD = "linear-gradient(90deg,#3ec7ff,#447dff)";
const NAVER_BG = "#03C75A";

const EMAIL_DOMAINS = ["gmail.com", "naver.com", "kakao.com", "직접 입력"];
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

const safeTrim = (s: string) => (s ?? "").trim();
const nfkc = (s: string) => (s ?? "").normalize("NFKC");
const isValidEmailFormat = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const hasTripleRepeat = (pw: string) => /(.)\1\1/.test(pw);

const hasSequentialRun = (pw: string, len = 3) => {
  const toCode = (c: string) => c.charCodeAt(0);
  for (let i = 0; i <= pw.length - len; i++) {
    const slice = pw.slice(i, i + len);
    let asc = true;
    let desc = true;
    for (let k = 1; k < slice.length; k++) {
      if (toCode(slice[k]) !== toCode(slice[k - 1]) + 1) asc = false;
      if (toCode(slice[k]) !== toCode(slice[k - 1]) - 1) desc = false;
      if (!asc && !desc) break;
    }
    if (asc || desc) return true;
  }
  return false;
};

const categoriesCount = (pw: string) =>
  [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].reduce((c, r) => c + (r.test(pw) ? 1 : 0), 0);

const containsEmailLocalPart = (pw: string, email: string) => {
  const local = (email.split("@")[0] ?? "").toLowerCase();
  return local ? pw.toLowerCase().includes(local) : false;
};

const validatePasswordPolicy = (pwRaw: string, emailRaw: string) => {
  const pw = nfkc(safeTrim(pwRaw));
  const email = nfkc(safeTrim(emailRaw));
  const msgs: string[] = [];
  if (pw.length < 8) msgs.push("8자 이상");
  if (categoriesCount(pw) < 3) msgs.push("대/소문자/숫자/특수 3종 이상");
  if (hasTripleRepeat(pw)) msgs.push("동일문자 3회 반복 금지");
  if (hasSequentialRun(pw)) msgs.push("연속문자 3자 이상 금지");
  if (containsEmailLocalPart(pw, email)) msgs.push("이메일 ID 포함 금지");
  return { ok: msgs.length === 0, messages: msgs };
};

const normalizePhone = (raw: string) => raw.replace(/[^0-9]/g, "");
const isLikelyKRPhone = (raw: string) => /^01[0-9][0-9]{7,8}$/.test(normalizePhone(raw));

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "expired-callback"?: () => void;
          "error-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
        }
      ) => string;
      remove?: (widgetId: string) => void;
      reset?: (widgetId?: string) => void;
    };
  }
}

function TurnstileBox({
  enabled,
  onTokenChange,
  resetKey,
}: {
  enabled: boolean;
  onTokenChange: (token: string) => void;
  resetKey: number;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (!TURNSTILE_SITE_KEY) return;
    if (!containerRef.current) return;

    const loadScript = () =>
      new Promise<void>((resolve) => {
        const existed = document.querySelector('script[data-turnstile="true"]') as HTMLScriptElement | null;
        if (existed) {
          if (window.turnstile) resolve();
          else existed.addEventListener("load", () => resolve(), { once: true });
          return;
        }

        const script = document.createElement("script");
        script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
        script.async = true;
        script.defer = true;
        script.dataset.turnstile = "true";
        script.onload = () => resolve();
        document.body.appendChild(script);
      });

    let mounted = true;

    loadScript().then(() => {
      if (!mounted || !containerRef.current || !window.turnstile) return;

      containerRef.current.innerHTML = "";
      widgetIdRef.current = window.turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        theme: "dark",
        callback: (token: string) => onTokenChange(token),
        "expired-callback": () => onTokenChange(""),
        "error-callback": () => onTokenChange(""),
      });
    });

    return () => {
      mounted = false;
      if (widgetIdRef.current && window.turnstile?.remove) {
        window.turnstile.remove(widgetIdRef.current);
      }
      widgetIdRef.current = null;
    };
  }, [enabled, onTokenChange]);

  useEffect(() => {
    if (!enabled) return;
    if (!widgetIdRef.current) return;
    if (!window.turnstile?.reset) return;

    onTokenChange("");
    window.turnstile.reset(widgetIdRef.current);
  }, [resetKey, enabled, onTokenChange]);

  if (!TURNSTILE_SITE_KEY) {
    return (
      <Alert severity="warning" sx={{ borderRadius: 2 }}>
        REACT_APP_TURNSTILE_SITE_KEY 가 설정되지 않았습니다.
      </Alert>
    );
  }

  return <Box ref={containerRef} />;
}

const Card = styled(Paper)({
  borderRadius: 18,
  padding: 16,
  border: `1px solid ${BORDER}`,
  background: CARD_BG,
  boxShadow: "0 18px 40px rgba(8,20,40,.45)",
});

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: SURFACE,
    color: TEXT_MAIN,
    "& fieldset": { borderColor: BORDER },
    "&:hover fieldset": { borderColor: "#7fb0ff" },
    "&.Mui-focused fieldset": {
      borderColor: "#7fb0ff",
      boxShadow: "0 0 0 3px rgba(127,176,255,.18)",
    },
    "& .MuiInputAdornment-root, & input, & textarea": { color: TEXT_MAIN },
  },
  "& .MuiFormHelperText-root": { marginLeft: 0, color: TEXT_SUB },
};

const btnPrimary = {
  height: 44,
  borderRadius: 12,
  fontWeight: 900,
  textTransform: "none" as const,
  color: "#0b1522",
  background: PRIMARY_GRAD,
  border: "1px solid rgba(62,199,255,.4)",
  boxShadow: "0 8px 20px rgba(62,199,255,.25)",
  "&:hover": { filter: "brightness(1.05)", boxShadow: "0 10px 24px rgba(62,199,255,.28)" },
};

const btnOutline = {
  height: 44,
  borderRadius: 12,
  fontWeight: 800,
  textTransform: "none" as const,
  color: TEXT_MAIN,
  border: `1px solid ${BORDER}`,
  background: "transparent",
  "&:hover": { borderColor: "#7fb0ff", boxShadow: "0 6px 16px rgba(16,24,40,.25)" },
};

const linkBtnSx = {
  minWidth: 0,
  px: 0.75,
  py: 0.25,
  fontSize: 12,
  fontWeight: 900,
  textTransform: "none" as const,
  color: "#a7d8ff",
  "&:hover": { background: alpha("#7fb0ff", 0.12) },
};

const socialBtnSx = {
  height: 42,
  borderRadius: 12,
  fontWeight: 900,
  textTransform: "none" as const,
};

const infoBoxSx = {
  mt: 1.25,
  p: 1.25,
  borderRadius: 12,
  border: `1px solid ${SUB_BORDER}`,
  background: alpha("#0e1a2b", 0.7),
  color: TEXT_SUB,
  fontSize: 12,
  lineHeight: 1.5,
};

const AuthModal: React.FC<AuthModalProps> = ({
  open,
  onClose,
  mode,
  isEditMode = false,
  userEmail = "",
  onPasswordUpdate,
  onLogin,
  onSignup,
  onGoogleLogin,
  onKakaoLogin,
  onNaverLogin,
  loading = false,
  contextTitle = "",
  contextDescription = "",
  onReady,
  onDismiss,
  onTabChange,
}) => {
  const dispatch = useDispatch();
  const loginError = useSelector((s: RootState) => s.auth.loginError);
  const signupError = useSelector((s: RootState) => s.auth.signupError);
  const signupStatus = useSelector((s: RootState) => s.auth.signupStatus);

  const findEmailLoading = useSelector((s: RootState) => s.auth.findEmailLoading);
  const findEmailError = useSelector((s: RootState) => s.auth.findEmailError);
  const maskedEmail = useSelector((s: RootState) => s.auth.maskedEmail);

  const resetLoading = useSelector((s: RootState) => s.auth.requestResetLoading);
  const resetError = useSelector((s: RootState) => s.auth.requestResetError);
  const resetRequested = useSelector((s: RootState) => s.auth.resetRequested);

  const actualMode: AuthModalMode = mode ?? (isEditMode ? "password-edit" : "auth");
  const isPasswordEditMode = actualMode === "password-edit";

  const [tab, setTab] = useState(0);

  const [emailId, setEmailId] = useState("");
  const [emailDomain, setEmailDomain] = useState(EMAIL_DOMAINS[0]);
  const [customDomain, setCustomDomain] = useState("");

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");

  const [marketingEmailAgree, setMarketingEmailAgree] = useState(false);
  const [marketingSmsAgree, setMarketingSmsAgree] = useState(false);
  const [termsAgree, setTermsAgree] = useState(false);
  const [privacyAgree, setPrivacyAgree] = useState(false);
  const [disclaimerAgree, setDisclaimerAgree] = useState(false);
  const [age14PlusAgree, setAge14PlusAgree] = useState(false);

  const [oldPasswordEdit, setOldPasswordEdit] = useState("");
  const [newPasswordEdit, setNewPasswordEdit] = useState("");
  const [confirmNewEdit, setConfirmNewEdit] = useState("");
  const [showPw, setShowPw] = useState(false);

  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [pwErr, setPwErr] = useState<string | null>(null);
  const [confirmPwErr, setConfirmPwErr] = useState<string | null>(null);
  const [agreeErr, setAgreeErr] = useState<string | null>(null);

  const [recoveryMode, setRecoveryMode] = useState<RecoveryMode>(null);
  const [recoveryKey, setRecoveryKey] = useState("");
  const [recoveryLocalErr, setRecoveryLocalErr] = useState<string | null>(null);

  const [legalOpen, setLegalOpen] = useState(false);
  const [legalType, setLegalType] = useState<LegalDocType>("terms");

  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileErr, setTurnstileErr] = useState<string | null>(null);
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const signupSubmittingRef = useRef(false);
  const readyReportedRef = useRef(false);

  const isSignupMode = !isPasswordEditMode && tab === 1;
  const isLoginMode = !isPasswordEditMode && tab === 0;
  const showTopError =
    !isPasswordEditMode && !recoveryMode && (isSignupMode ? signupError : loginError);
  const busy = loading || findEmailLoading || resetLoading;

  useEffect(() => {
    if (!open) {
      readyReportedRef.current = false;
      return;
    }
    if (isPasswordEditMode || readyReportedRef.current) return;

    if (typeof window === "undefined") {
      readyReportedRef.current = true;
      onReady?.();
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      if (readyReportedRef.current) return;
      readyReportedRef.current = true;
      onReady?.();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isPasswordEditMode, onReady, open]);

  const fullEmail = useMemo(() => {
    const id = nfkc(safeTrim(emailId));
    const domain =
      emailDomain === "직접 입력" ? nfkc(safeTrim(customDomain)) : nfkc(safeTrim(emailDomain));
    return id && domain ? `${id}@${domain}` : "";
  }, [emailId, emailDomain, customDomain]);

  const accountEmail = useMemo(() => {
    return nfkc(safeTrim(fullEmail || userEmail));
  }, [fullEmail, userEmail]);

  const clearLocalValidation = () => {
    setEmailErr(null);
    setPwErr(null);
    setConfirmPwErr(null);
    setAgreeErr(null);
    setRecoveryLocalErr(null);
    setTurnstileErr(null);
  };

  const handleGoogleAuth = () => onGoogleLogin?.();
    const handleNaverAuth = () => onNaverLogin?.();

  const normalizeEmailInput = (raw: string) => {
    const v = nfkc(raw);
    if (!v.includes("@")) return;
    const [id, domainRaw] = v.split("@");
    const domain = (domainRaw || "").trim();
    setEmailId((id || "").trim());

    if (domain && EMAIL_DOMAINS.includes(domain)) {
      setEmailDomain(domain);
      setCustomDomain("");
    } else if (domain) {
      setEmailDomain("직접 입력");
      setCustomDomain(domain);
    }
  };

  useEffect(() => {
    if (isPasswordEditMode && userEmail) {
      const [id, domain] = userEmail.split("@");
      setEmailId(id || "");
      if (EMAIL_DOMAINS.includes(domain)) {
        setEmailDomain(domain);
        setCustomDomain("");
      } else {
        setEmailDomain("직접 입력");
        setCustomDomain(domain || "");
      }
    }
  }, [isPasswordEditMode, userEmail]);

  useEffect(() => {
    if (signupStatus === "success") {
      signupSubmittingRef.current = false;

      setTab(0);
      setPassword("");
      setConfirmPassword("");
      setTurnstileToken("");
      setTurnstileErr(null);
      setPwErr(null);
      setConfirmPwErr(null);
      setAgreeErr(null);
      return;
    }

    if (signupStatus === "fail") {
      signupSubmittingRef.current = false;

      // Turnstile token은 서버 검증 시 1회 소비될 수 있으므로
      // 실패 후 기존 token을 재사용하지 않고 새 token을 발급받는다.
      setTurnstileToken("");
      setTurnstileErr("봇 방지 검증을 다시 완료해 주세요.");
      setTurnstileResetKey((v) => v + 1);
    }
  }, [signupStatus]);

  useEffect(() => {
    if (!isSignupMode) {
      signupSubmittingRef.current = false;
      setTurnstileToken("");
      setTurnstileErr(null);
    }
  }, [isSignupMode]);

  const resetAll = () => {
    setEmailId("");
    setEmailDomain(EMAIL_DOMAINS[0]);
    setCustomDomain("");
    setPassword("");
    setConfirmPassword("");
    setPhone("");

    setMarketingEmailAgree(false);
    setMarketingSmsAgree(false);
    setTermsAgree(false);
    setPrivacyAgree(false);
    setDisclaimerAgree(false);
    setAge14PlusAgree(false);

    setOldPasswordEdit("");
    setNewPasswordEdit("");
    setConfirmNewEdit("");

    clearLocalValidation();

    setShowPw(false);
    setTab(0);

    setLegalOpen(false);
    setLegalType("terms");

    setRecoveryMode(null);
    setRecoveryKey("");
    dispatch(clearRecoveryState());

    signupSubmittingRef.current = false;
    setTurnstileToken("");
  };

  const handleClose = (dismissReason = "cancel-button") => {
    if (!isPasswordEditMode) {
      onDismiss?.({
        reason: dismissReason,
        tab: isSignupMode ? "signup" : "login",
      });
    }
    resetAll();
    onClose();
  };

  const handleDialogClose = (_event: object, reason: "backdropClick" | "escapeKeyDown") => {
    handleClose(reason);
  };

  const openLegal = (type: LegalDocType) => {
    setLegalType(type);
    setLegalOpen(true);
  };

  const changeAuthTab = (v: number) => {
    const next = v === 1 ? 1 : 0;
    if (next === tab) return;

    setTab(next);
    onTabChange?.(next === 1 ? "signup" : "login");
    dispatch(clearRecoveryState());
    setRecoveryMode(null);
    setRecoveryKey("");
    clearLocalValidation();
    setPassword("");
    setConfirmPassword("");
  };

  const handleTabChange = (_: React.SyntheticEvent, v: number) => {
    changeAuthTab(v);
  };

  const handlePasswordUpdate = () => {
    setPwErr(null);
    setConfirmPwErr(null);

    const emailForPolicy = accountEmail;

    if (!emailForPolicy || !isValidEmailFormat(emailForPolicy)) {
      setEmailErr("유효한 계정 이메일이 필요합니다.");
      return;
    }
    if (!oldPasswordEdit) {
      setPwErr("현재 비밀번호를 입력하세요.");
      return;
    }

    const v = validatePasswordPolicy(newPasswordEdit, emailForPolicy);
    if (!v.ok) {
      setPwErr(v.messages.join(" · "));
      return;
    }
    if (newPasswordEdit !== confirmNewEdit) {
      setConfirmPwErr("새 비밀번호 확인이 일치하지 않습니다.");
      return;
    }

    onPasswordUpdate?.({
      oldPassword: nfkc(oldPasswordEdit),
      newPassword: nfkc(newPasswordEdit),
    });
  };

  const handleSignup = () => {
    clearLocalValidation();

    const email = nfkc(safeTrim(fullEmail));
    const pw = nfkc(safeTrim(password));
    const cfm = nfkc(safeTrim(confirmPassword));
    const ph = normalizePhone(phone);

    if (!email) {
      setEmailErr("이메일을 입력하세요.");
      return;
    }
    if (!isValidEmailFormat(email)) {
      setEmailErr("올바른 이메일 형식이 아닙니다.");
      return;
    }

    if (REQUIRE_PHONE_ON_SIGNUP && !ph) {
      setAgreeErr("휴대폰 번호를 입력하세요.");
      return;
    }

    if (ph && !isLikelyKRPhone(ph)) {
      setAgreeErr("휴대폰 번호 형식이 올바르지 않습니다. (예: 01012345678)");
      return;
    }

    if (!termsAgree || !privacyAgree || !disclaimerAgree) {
      setAgreeErr("필수 항목(이용약관/개인정보처리방침/서비스 이용 안내)에 동의해야 합니다.");
      return;
    }

    if (ADULT_ONLY_14_PLUS && !age14PlusAgree) {
      setAgreeErr("만 14세 이상만 가입할 수 있습니다.");
      return;
    }

    const verifiedTurnstileToken = turnstileToken.trim();

    if (!verifiedTurnstileToken) {
      setTurnstileErr("봇 방지 검증을 완료해 주세요.");
      return;
    }

    const v = validatePasswordPolicy(pw, email);
    if (!v.ok) {
      setPwErr(v.messages.join(" · "));
      return;
    }
    if (pw !== cfm) {
      setConfirmPwErr("비밀번호 확인이 일치하지 않습니다.");
      return;
    }

    if (!onSignup) {
      return;
    }

    if (signupSubmittingRef.current) {
      return;
    }

    signupSubmittingRef.current = true;

    // 현재 token은 이번 요청 한 번에만 사용한다.
    // 즉시 state에서 제거해 같은 token의 재전송을 차단한다.
    setTurnstileToken("");
    setTurnstileErr(null);

    onSignup({
      email,
      password: pw,
      confirmPassword: cfm,
      phone: ph || "",
      marketingEmailAgree,
      marketingSmsAgree,
      termsAgree,
      privacyAgree,
      disclaimerAgree,
      age14PlusAgree,
      turnstileToken: verifiedTurnstileToken,
      consentVersions: {
        terms: LEGAL_VERSIONS.TERMS,
        privacy: LEGAL_VERSIONS.PRIVACY,
        disclaimer: LEGAL_VERSIONS.DISCLAIMER,
        ageOver14: LEGAL_VERSIONS.AGE_OVER_14,
        marketing: LEGAL_VERSIONS.MARKETING,
      },
    });
  };

  const handleLogin = () => {
    clearLocalValidation();

    const email = nfkc(safeTrim(fullEmail));
    const pw = nfkc(safeTrim(password));

    if (!email) {
      setEmailErr("이메일을 입력하세요.");
      return;
    }
    if (!isValidEmailFormat(email)) {
      setEmailErr("올바른 이메일 형식이 아닙니다.");
      return;
    }
    if (!pw) {
      setPwErr("비밀번호를 입력하세요.");
      return;
    }

    onLogin?.({ email, password: pw });
  };

  const startRecovery = (mode: RecoveryMode) => {
    dispatch(clearRecoveryState());
    clearLocalValidation();
    setRecoveryMode(mode);

    if (mode === "reset-pw") {
      const em = nfkc(safeTrim(fullEmail));
      setRecoveryKey(em || "");
    } else {
      setRecoveryKey("");
    }
  };

  const backToNormal = () => {
    dispatch(clearRecoveryState());
    clearLocalValidation();
    setRecoveryMode(null);
    setRecoveryKey("");
  };

  const submitRecovery = () => {
    setRecoveryLocalErr(null);

    if (recoveryMode === "find-id") {
      const ph = normalizePhone(recoveryKey);
      if (!ph) {
        setRecoveryLocalErr("휴대폰 번호를 입력하세요.");
        return;
      }
      if (!isLikelyKRPhone(ph)) {
        setRecoveryLocalErr("휴대폰 번호 형식이 올바르지 않습니다. (예: 01012345678)");
        return;
      }
      dispatch(findEmailRequest({ phone: ph }));
      return;
    }

    if (recoveryMode === "reset-pw") {
      const em = nfkc(safeTrim(recoveryKey));
      if (!em) {
        setRecoveryLocalErr("이메일을 입력하세요.");
        return;
      }
      if (!isValidEmailFormat(em)) {
        setRecoveryLocalErr("올바른 이메일 형식이 아닙니다.");
        return;
      }
      dispatch(requestPasswordResetRequest({ email: em }));
    }
  };

  const modalTitle = isPasswordEditMode ? "비밀번호 변경" : "계정 액세스";

  return (
    <>
      <GlobalStyles
        styles={{
          body: { color: TEXT_MAIN },
          "input:-webkit-autofill, textarea:-webkit-autofill, select:-webkit-autofill": {
            WebkitBoxShadow: `0 0 0px 1000px ${SURFACE} inset`,
            WebkitTextFillColor: TEXT_MAIN,
            transition: "background-color 9999s ease-out 0s",
          },
        }}
      />

      <LegalDocumentDialog
        open={legalOpen}
        onClose={() => setLegalOpen(false)}
        type={legalType}
      />

      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{ elevation: 0, sx: { borderRadius: 0, background: "transparent" } }}
        BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,.55)" } }}
      >
        <Card>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
            <Box sx={{ fontWeight: 900, letterSpacing: -0.2, color: TEXT_MAIN }}>{modalTitle}</Box>

            {!isPasswordEditMode && (
              <Tabs
                value={tab}
                onChange={handleTabChange}
                sx={{
                  minHeight: 36,
                  "& .MuiTabs-indicator": { height: 3, borderRadius: 3, background: "#7fb7ff" },
                  "& .MuiTab-root": {
                    minHeight: 36,
                    textTransform: "none",
                    fontWeight: 800,
                    color: TEXT_SUB,
                    "&.Mui-selected": { color: TEXT_MAIN },
                  },
                }}
              >
                <Tab label="로그인" />
                <Tab label="회원가입" />
              </Tabs>
            )}
          </Box>

          {!isPasswordEditMode && contextTitle && (
            <Box
              sx={{
                mb: 1.25,
                p: 1.25,
                borderRadius: 2,
                border: `1px solid ${SUB_BORDER}`,
                background: alpha("#7fb0ff", 0.08),
              }}
            >
              <Typography sx={{ color: TEXT_MAIN, fontSize: 13.5, fontWeight: 900, lineHeight: 1.45 }}>
                {contextTitle}
              </Typography>
              {contextDescription && (
                <Typography sx={{ mt: 0.35, color: TEXT_SUB, fontSize: 12, lineHeight: 1.5 }}>
                  {contextDescription}
                </Typography>
              )}
            </Box>
          )}

          {showTopError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {showTopError}
            </Alert>
          )}

          {signupStatus === "success" && !isPasswordEditMode && !recoveryMode && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              회원가입 요청이 완료되었습니다. 이메일 인증 링크를 확인한 뒤 로그인해 주세요.
            </Alert>
          )}

          <DialogContent sx={{ px: 0, pt: 1, pb: 0 }}>
            {!isPasswordEditMode && isLoginMode && recoveryMode && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ color: TEXT_MAIN, fontWeight: 900, fontSize: 16, mb: 1 }}>
                  {recoveryMode === "find-id" ? "아이디(이메일) 찾기" : "비밀번호 재설정"}
                </Typography>

                {recoveryMode === "find-id" && (
                  <>
                    <TextField
                      fullWidth
                      placeholder="가입 시 사용한 휴대폰 번호"
                      value={recoveryKey}
                      onChange={(e) => setRecoveryKey(e.target.value)}
                      sx={fieldSx}
                      helperText="예) 01012345678"
                      disabled={busy}
                    />

                    <Button fullWidth sx={{ ...btnPrimary, mt: 1 }} disabled={busy} onClick={submitRecovery}>
                      아이디 안내 요청
                    </Button>

                    <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
                      보안을 위해, 계정 존재 여부와 관계없이 동일한 안내를 제공합니다.
                      <br />
                      입력하신 정보와 일치하는 계정이 있는 경우 안내가 제공됩니다.
                    </Alert>

                    {findEmailError && (
                      <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                        {findEmailError}
                      </Alert>
                    )}

                    {maskedEmail && (
                      <Alert severity="success" sx={{ mt: 1.5, borderRadius: 2 }}>
                        가입된 이메일은 <b>{maskedEmail}</b> 입니다.
                      </Alert>
                    )}
                  </>
                )}

                {recoveryMode === "reset-pw" && (
                  <>
                    <TextField
                      fullWidth
                      placeholder="가입한 이메일 주소"
                      value={recoveryKey}
                      onChange={(e) => setRecoveryKey(e.target.value)}
                      sx={fieldSx}
                      helperText="재설정 링크를 이메일로 발송합니다."
                      error={!!recoveryKey && !isValidEmailFormat(nfkc(safeTrim(recoveryKey)))}
                      disabled={busy}
                    />

                    <Button fullWidth sx={{ ...btnPrimary, mt: 1 }} disabled={busy} onClick={submitRecovery}>
                      재설정 링크 발송
                    </Button>

                    <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
                      보안을 위해, 계정 존재 여부와 관계없이 동일한 안내를 제공합니다.
                      <br />
                      등록된 계정이 있다면 재설정 링크가 발송됩니다.
                    </Alert>

                    {resetError && (
                      <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                        {resetError}
                      </Alert>
                    )}

                    {resetRequested && (
                      <Alert severity="success" sx={{ mt: 1.5, borderRadius: 2 }}>
                        재설정 링크 발송 요청이 처리되었습니다. 메일함을 확인해주세요.
                      </Alert>
                    )}
                  </>
                )}

                {recoveryLocalErr && (
                  <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>
                    {recoveryLocalErr}
                  </Alert>
                )}

                <Button size="small" sx={{ ...linkBtnSx, mt: 1 }} onClick={backToNormal} disabled={busy}>
                  ← 로그인으로 돌아가기
                </Button>

                <Divider sx={{ my: 2, borderColor: SUB_BORDER }} />
              </Box>
            )}

            {!isPasswordEditMode && !recoveryMode && (
              <>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    fullWidth
                    placeholder="이메일 ID"
                    value={emailId}
                    onChange={(e) => {
                      setEmailId(e.target.value);
                      if (e.target.value.includes("@")) normalizeEmailInput(e.target.value);
                    }}
                    onBlur={() => normalizeEmailInput(emailId)}
                    sx={fieldSx}
                    error={!!emailErr}
                    InputProps={{ endAdornment: <InputAdornment position="end">@</InputAdornment> }}
                    disabled={busy}
                  />
                  <TextField
                    select
                    value={emailDomain}
                    onChange={(e) => setEmailDomain(e.target.value)}
                    sx={{ ...fieldSx, minWidth: 150 }}
                    disabled={busy}
                  >
                    {EMAIL_DOMAINS.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {emailDomain === "직접 입력" && (
                  <TextField
                    fullWidth
                    placeholder="예) mycompany.com"
                    value={customDomain}
                    onChange={(e) => setCustomDomain(e.target.value)}
                    sx={{ ...fieldSx, mt: 1 }}
                    error={!!emailErr}
                    disabled={busy}
                  />
                )}

                {emailErr && (
                  <FormHelperText error sx={{ mt: 0.5 }}>
                    {emailErr}
                  </FormHelperText>
                )}
              </>
            )}

            <Box sx={{ mt: 2 }}>
              {!isPasswordEditMode && !recoveryMode && (
                <>
                  <TextField
                    fullWidth
                    type={showPw ? "text" : "password"}
                    placeholder="비밀번호"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={fieldSx}
                    error={!!pwErr}
                    helperText={pwErr || "8자 이상 · 대/소문자/숫자/특수 3종 이상 · 연속/반복/ID 금지"}
                    disabled={busy}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPw((v) => !v)}
                            size="small"
                            sx={{ color: TEXT_SUB }}
                          >
                            {showPw ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  {isSignupMode && (
                    <TextField
                      fullWidth
                      type={showPw ? "text" : "password"}
                      placeholder="비밀번호 확인"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      sx={{ ...fieldSx, mt: 1 }}
                      error={!!confirmPwErr}
                      helperText={confirmPwErr || ""}
                      disabled={busy}
                    />
                  )}
                </>
              )}

              {isPasswordEditMode && (
                <>
                  <TextField
                    fullWidth
                    label="계정 이메일"
                    value={userEmail}
                    sx={fieldSx}
                    disabled
                    InputProps={{ readOnly: true }}
                  />

                  <TextField
                    fullWidth
                    type="password"
                    placeholder="현재 비밀번호"
                    value={oldPasswordEdit}
                    onChange={(e) => setOldPasswordEdit(e.target.value)}
                    sx={{ ...fieldSx, mt: 1 }}
                    disabled={busy}
                  />

                  <TextField
                    fullWidth
                    type={showPw ? "text" : "password"}
                    placeholder="새 비밀번호"
                    value={newPasswordEdit}
                    onChange={(e) => setNewPasswordEdit(e.target.value)}
                    sx={{ ...fieldSx, mt: 1 }}
                    error={!!pwErr}
                    helperText={pwErr || "8자 이상 · 대/소문자/숫자/특수 3종 이상 · 연속/반복/ID 금지"}
                    disabled={busy}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPw((v) => !v)}
                            size="small"
                            sx={{ color: TEXT_SUB }}
                          >
                            {showPw ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    fullWidth
                    type={showPw ? "text" : "password"}
                    placeholder="새 비밀번호 확인"
                    value={confirmNewEdit}
                    onChange={(e) => setConfirmNewEdit(e.target.value)}
                    sx={{ ...fieldSx, mt: 1 }}
                    error={!!confirmPwErr}
                    helperText={confirmPwErr || ""}
                    disabled={busy}
                  />
                </>
              )}
            </Box>

            {!isPasswordEditMode && isSignupMode && !recoveryMode && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  type="tel"
                  placeholder={`휴대폰 번호 ${REQUIRE_PHONE_ON_SIGNUP ? "(필수)" : "(선택)"}`}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  sx={fieldSx}
                  disabled={busy}
                  helperText={
                    REQUIRE_PHONE_ON_SIGNUP
                      ? "가입 및 계정 보호를 위해 휴대폰 번호를 입력하세요. 예) 01012345678"
                      : "선택 입력 항목입니다. 계정 보호 및 중요 알림에 사용할 수 있습니다. 예) 01012345678"
                  }
                  error={!!phone && !isLikelyKRPhone(phone)}
                />

                <Box sx={{ mt: 1.25 }}>
                  <Typography sx={{ color: TEXT_MAIN, fontWeight: 900, fontSize: 13, mb: 0.75 }}>
                    봇 방지 검증
                  </Typography>
                  <TurnstileBox
                    enabled={open && isSignupMode}
                    onTokenChange={setTurnstileToken}
                    resetKey={turnstileResetKey}
                  />
                  {turnstileErr && (
                    <FormHelperText error sx={{ mt: 0.5, ml: 0 }}>
                      {turnstileErr}
                    </FormHelperText>
                  )}
                </Box>

                <Box sx={{ mt: 1, display: "grid", rowGap: 0.5, color: TEXT_MAIN }}>
                  <FormControlLabel
                    control={<Checkbox checked={termsAgree} onChange={(e) => setTermsAgree(e.target.checked)} sx={{ color: TEXT_SUB }} />}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <span>이용약관 동의 (필수)</span>
                        <Button size="small" sx={linkBtnSx} onClick={() => openLegal("terms")}>전문 보기</Button>
                      </Box>
                    }
                  />

                  <FormControlLabel
                    control={<Checkbox checked={privacyAgree} onChange={(e) => setPrivacyAgree(e.target.checked)} sx={{ color: TEXT_SUB }} />}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <span>개인정보처리방침 동의 (필수)</span>
                        <Button size="small" sx={linkBtnSx} onClick={() => openLegal("privacy")}>전문 보기</Button>
                      </Box>
                    }
                  />

                  <FormControlLabel
                    control={<Checkbox checked={disclaimerAgree} onChange={(e) => setDisclaimerAgree(e.target.checked)} sx={{ color: TEXT_SUB }} />}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                        <span>서비스 이용 안내 확인 (필수)</span>
                        <Button size="small" sx={linkBtnSx} onClick={() => openLegal("notice")}>전문 보기</Button>
                      </Box>
                    }
                  />

                  <FormControlLabel
                    control={<Checkbox checked={age14PlusAgree} onChange={(e) => setAge14PlusAgree(e.target.checked)} sx={{ color: TEXT_SUB }} />}
                    label="만 14세 이상입니다. (필수)"
                  />

                  <FormControlLabel
                    control={<Checkbox checked={marketingEmailAgree} onChange={(e) => setMarketingEmailAgree(e.target.checked)} sx={{ color: TEXT_SUB }} />}
                    label="광고성 정보 수신 동의 - 이메일 (선택)"
                  />

                  <FormControlLabel
                    control={<Checkbox checked={marketingSmsAgree} onChange={(e) => setMarketingSmsAgree(e.target.checked)} sx={{ color: TEXT_SUB }} />}
                    label="광고성 정보 수신 동의 - 문자메시지 (선택)"
                  />

                  <Typography sx={{ color: TEXT_SUB, fontSize: 12, mt: 0.25, ml: 0.5 }}>
                    이벤트, 혜택, 신규 서비스 및 프로모션 안내를 위해 사용됩니다. 동의하지 않아도 서비스 이용에는 제한이 없으며, 언제든지 철회할 수 있습니다.
                  </Typography>

                  {agreeErr && (
                    <FormHelperText error sx={{ ml: 0 }}>
                      {agreeErr}
                    </FormHelperText>
                  )}

                  <Box sx={infoBoxSx}>
                    <b style={{ color: TEXT_MAIN }}>중요 안내</b>
                    <br />
                    본 서비스는 정책·이슈·시장 데이터를 정리하고 시각화한 참고용 정보 서비스입니다.
                    <br />
                    서비스 내 자료, 분석, 점수, 분류 및 과거 데이터 비교는 이용자의 이해를 돕기 위한 참고자료입니다.
                    <br />
                    중요한 판단 전에는 공시, 원문 기사, 정책 자료 등 원자료를 추가 확인하시기 바랍니다.
                  </Box>
                </Box>
              </Box>
            )}

            {!isPasswordEditMode && isLoginMode && !recoveryMode && (
              <>
                <Divider sx={{ my: 2, borderColor: SUB_BORDER }}>
                  또는 소셜 계정으로 로그인
                </Divider>

                <Box sx={{ display: "grid", gap: 1 }}>
                  <Button
                    onClick={handleGoogleAuth}
                    fullWidth
                    startIcon={<GoogleIcon />}
                    disabled={busy || !onGoogleLogin}
                    sx={{ ...socialBtnSx, ...btnOutline }}
                  >
                    Google로 로그인
                  </Button>

                  <Button
                    onClick={handleNaverAuth}
                    fullWidth
                    startIcon={<LoginRoundedIcon />}
                    disabled={busy || !onNaverLogin}
                    sx={{
                      ...socialBtnSx,
                      background: NAVER_BG,
                      color: "white",
                      "&:hover": { background: "#02b350" },
                    }}
                  >
                    네이버로 로그인
                  </Button>
                </Box>
              </>
            )}

            <Divider sx={{ my: 2, borderColor: SUB_BORDER }} />

            <Box sx={{ display: "flex", gap: 1 }}>
              <Button onClick={() => handleClose("cancel-button")} disabled={busy} fullWidth sx={btnOutline}>
                취소
              </Button>
              <Button
                disabled={busy || (!isPasswordEditMode && !!recoveryMode)}
                fullWidth
                onClick={isPasswordEditMode ? handlePasswordUpdate : isLoginMode ? handleLogin : handleSignup}
                sx={btnPrimary}
              >
                {isPasswordEditMode ? "비밀번호 변경" : isLoginMode ? "로그인" : "가입하기"}
              </Button>
            </Box>

            {!isPasswordEditMode && isLoginMode && !recoveryMode && (
              <Box sx={{ mt: 1.25, textAlign: "center", color: TEXT_SUB, fontSize: 12 }}>
                <Button size="small" sx={{ ...linkBtnSx, mr: 1 }} onClick={() => startRecovery("find-id")} disabled={busy}>
                  아이디 찾기
                </Button>
                <span style={{ opacity: 0.55 }}>|</span>
                <Button size="small" sx={{ ...linkBtnSx, ml: 1 }} onClick={() => startRecovery("reset-pw")} disabled={busy}>
                  비밀번호 찾기
                </Button>
              </Box>
            )}

            {!isPasswordEditMode && !recoveryMode && (
              <Box sx={{ mt: 1.25, textAlign: "center", color: TEXT_SUB, fontSize: 12 }}>
                {isLoginMode ? "계정이 없으신가요?" : "이미 계정이 있으신가요?"}{" "}
                <Button
                  size="small"
                  onClick={() => changeAuthTab(isLoginMode ? 1 : 0)}
                  sx={{ textTransform: "none", fontWeight: 800, px: 0.5, color: "#a7d8ff" }}
                  disabled={busy}
                >
                  {isLoginMode ? "회원가입" : "로그인"}
                </Button>
              </Box>
            )}
          </DialogContent>
        </Card>
      </Dialog>
    </>
  );
};

export default AuthModal;
